import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '@/lib/api/client';

export type AsyncState<T> =
  | { status: 'loading' }
  | { status: 'ready'; data: T }
  | { status: 'denied' }
  | { status: 'not-implemented' }
  | { status: 'error'; message: string };

/**
 * Runs a promise-returning loader and maps its failure modes onto the states
 * the UI actually distinguishes — including `denied` (BR-AP1) and
 * `not-implemented`, so a scaffolded endpoint never renders as a crash.
 *
 * `deps` controls when the loader re-runs, exactly like an effect dependency
 * list; `reload` re-runs it on demand.
 */
export function useAsync<T>(
  loader: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[] = [],
): { state: AsyncState<T>; reload: () => void } {
  const [state, setState] = useState<AsyncState<T>>({ status: 'loading' });
  const [nonce, setNonce] = useState(0);

  // The loader is typically an inline arrow; the dependency list passed by the
  // caller is what decides identity, so it is the honest dependency here.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const run = useCallback(loader, deps);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setState({ status: 'loading' });
    run(controller.signal)
      .then((data) => {
        if (active) setState({ status: 'ready', data });
      })
      .catch((cause: unknown) => {
        if (!active || controller.signal.aborted) return;
        setState(toFailure(cause));
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [run, nonce]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  return { state, reload };
}

export function toFailure(cause: unknown): Extract<
  AsyncState<never>,
  { status: 'denied' | 'not-implemented' | 'error' }
> {
  if (cause instanceof ApiError) {
    if (cause.isDenied) return { status: 'denied' };
    if (cause.isNotImplemented) return { status: 'not-implemented' };
    return { status: 'error', message: cause.message };
  }
  return { status: 'error', message: 'Something went wrong.' };
}
